export type QuoteLineColumnId =
  | 'lineType'
  | 'partNumber'
  | 'description'
  | 'quantity'
  | 'unitValue'
  | 'discount'
  | 'free'
  | 'lineTotal';

export type QuoteLineColumnAlign = 'left' | 'center' | 'right';

export interface QuoteLineColumnConfig {
  id: QuoteLineColumnId;
  label: string;
  included: boolean;
  align: QuoteLineColumnAlign;
}

export const DEFAULT_QUOTE_LINE_COLUMNS: QuoteLineColumnConfig[] = [
  { id: 'lineType', label: 'Type', included: true, align: 'left' },
  { id: 'partNumber', label: 'Part Number', included: true, align: 'left' },
  { id: 'description', label: 'Description', included: true, align: 'left' },
  { id: 'quantity', label: 'Qty', included: true, align: 'right' },
  { id: 'unitValue', label: 'Unit', included: true, align: 'right' },
  { id: 'discount', label: 'Discount', included: false, align: 'right' },
  { id: 'free', label: 'Free / Covered', included: false, align: 'center' },
  { id: 'lineTotal', label: 'Total', included: true, align: 'right' }
];

export const normalizeQuoteLineColumns = (value?: unknown): QuoteLineColumnConfig[] => {
  const rawColumns = Array.isArray(value) ? value : [];
  const byId = new Map<string, any>();
  rawColumns.forEach(column => {
    const id = String((column as any)?.id || '').trim();
    if (id) byId.set(id, column);
  });

  const normalizeColumn = (defaultColumn: QuoteLineColumnConfig): QuoteLineColumnConfig => {
    const raw = byId.get(defaultColumn.id);
    const align = String(raw?.align || defaultColumn.align).trim().toLowerCase();
    return {
      id: defaultColumn.id,
      label: String(raw?.label || defaultColumn.label).trim() || defaultColumn.label,
      included: raw?.included === undefined ? defaultColumn.included : raw.included !== false,
      align: align === 'center' || align === 'right' ? align : defaultColumn.align
    };
  };

  if (rawColumns.length === 0) {
    return DEFAULT_QUOTE_LINE_COLUMNS.map(normalizeColumn);
  }

  const defaultById = new Map(DEFAULT_QUOTE_LINE_COLUMNS.map(column => [column.id, column]));
  const orderedColumns: QuoteLineColumnConfig[] = [];
  const seen = new Set<string>();

  rawColumns.forEach(rawColumn => {
    const id = String((rawColumn as any)?.id || '').trim() as QuoteLineColumnId;
    const defaultColumn = defaultById.get(id);
    if (!defaultColumn || seen.has(id)) return;
    orderedColumns.push(normalizeColumn(defaultColumn));
    seen.add(id);
  });

  DEFAULT_QUOTE_LINE_COLUMNS.forEach(defaultColumn => {
    if (!seen.has(defaultColumn.id)) {
      orderedColumns.push(normalizeColumn(defaultColumn));
    }
  });

  return orderedColumns;
};

export const getIncludedQuoteLineColumns = (value?: unknown): QuoteLineColumnConfig[] =>
  normalizeQuoteLineColumns(value).filter(column => column.included);

const formatFallbackCurrency = (value: number): string => `$${value.toFixed(2)}`;

export const getQuoteLineColumnDisplayValue = (
  line: any,
  columnId: QuoteLineColumnId,
  options: {
    formatCurrency?: (value: number) => string;
  } = {}
): string => {
  const formatCurrency = options.formatCurrency || formatFallbackCurrency;
  const quantity = Number(line?.quantity || 0);
  const unitValue = Number(line?.unitValue || 0);
  const lineTotal = line?.isFree ? 0 : Number((line?.lineTotal ?? quantity * unitValue) || 0);
  const originalUnitValue = Number(line?.originalUnitValue || 0);
  const discountPercent = Number(line?.slaDiscountPercent || 0);

  switch (columnId) {
    case 'lineType':
      return String(line?.lineType || 'part').toLowerCase();
    case 'partNumber':
      return String(line?.partNumber || line?.sku || '').trim() || '-';
    case 'description':
      return String(line?.description || '').trim() || '-';
    case 'quantity':
      return Number.isFinite(quantity) ? String(quantity) : '-';
    case 'unitValue':
      return line?.isFree ? 'FREE' : formatCurrency(Number.isFinite(unitValue) ? unitValue : 0);
    case 'discount':
      if (Number.isFinite(discountPercent) && discountPercent > 0) return `${discountPercent}%`;
      if (Number.isFinite(originalUnitValue) && originalUnitValue > 0 && originalUnitValue > unitValue) {
        const calculated = ((originalUnitValue - unitValue) / originalUnitValue) * 100;
        return `${Number(calculated.toFixed(1))}%`;
      }
      return '-';
    case 'free':
      if (line?.isFree) return 'Free';
      if (String(line?.slaCoverageStatus || '').toUpperCase() === 'COVERED') {
        return String(line?.slaLabel || '').trim() || 'Covered';
      }
      if (String(line?.slaCoverageStatus || '').toUpperCase() === 'DISCOUNTED') {
        return String(line?.slaLabel || '').trim() || 'Discounted';
      }
      return '-';
    case 'lineTotal':
      return formatCurrency(Number.isFinite(lineTotal) ? lineTotal : 0);
    default:
      return '-';
  }
};
